import type { ReactNode, JSX } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import type { LiveStreamEvent, RegistrationPage } from '~/graphql';
import { isNotNil } from '~/utilities/type-guards';
import { useInjectFonts } from '~/hooks/useInjectFonts';
import type { Font } from '~/utilities/fonts';
import type {
  EventBrandValuesSet,
  RegistrationCustomizationsWithGeneratedValues,
} from '../Brand/types';
import { useLiveStreamEventCustomizationsLazyQuery } from '../../Broadcast/gql/__gql/GetLiveEventCustomizations.gql.gen';
import { getThemeValuesAsCssVars } from '../utilities/utilities';
import { useRegistrationCustomizationStore } from './RegistrationCustomizationStore';
import { generateRegistrationThemeValues } from '../generateRegistrationThemeValues';
import { useBrandStore } from '../Brand/BrandStore';

const RegistrationCustomizationsContext =
  createContext<RegistrationCustomizationsWithGeneratedValues>(
    {} as RegistrationCustomizationsWithGeneratedValues,
  );

type Props = {
  children: ReactNode;
  eventHashedId: string;
  isInEditorMode: boolean;
};

export const SharedRegistrationCustomizationsProvider = ({
  children,
  eventHashedId,
  isInEditorMode,
}: Props): JSX.Element => {
  const {
    registrationPageCustomizationsPreview,
    setSavedCustomizations,
    isLoadingRegistrationCustomizations,
  } = useRegistrationCustomizationStore((state) => state);
  const initializeBrand = useBrandStore((state) => state.initializeBrand);
  const [liveStreamEventData, setLiveStreamEventData] = useState<LiveStreamEvent>();

  const [getLiveStreamEventCustomizations, { loading: isLoadingLiveStreamEventCustomizations }] =
    useLiveStreamEventCustomizationsLazyQuery({
      variables: { hashedId: eventHashedId },
      onCompleted: ({ liveStreamEvent }) => {
        setLiveStreamEventData(liveStreamEvent as LiveStreamEvent);
      },
    });

  useEffect(() => {
    if (!isInEditorMode) {
      // Only fetch the live stream event customizations if we're on one of the public-facing pages
      void getLiveStreamEventCustomizations();
    }
  }, [getLiveStreamEventCustomizations, isInEditorMode]);

  useEffect(() => {
    if (isNotNil(liveStreamEventData)) {
      const {
        description,
        customizations,
        eventThumbnailBakeryHashedId,
        eventThumbnailUrl,
        thumbnailAltText,
        registrationPageVideoHashedId,
      } = liveStreamEventData;
      const brandCustomizations = (customizations?.brand ?? {}) as EventBrandValuesSet;
      const registrationPageCustomizations = (customizations?.registrationPage ??
        {}) as RegistrationPage;

      // Make sure to initialize the brand first since the reg page customizations store depends on the brand
      initializeBrand(eventHashedId, brandCustomizations);
      setSavedCustomizations({
        description,
        eventThumbnailUrl: eventThumbnailUrl ?? null,
        eventThumbnailAlt: thumbnailAltText ?? null,
        eventThumbnailBakeryHashedId: eventThumbnailBakeryHashedId ?? null,
        registrationPageCustomizations,
        heroVideoHashedId: registrationPageVideoHashedId,
      });
    }
  }, [eventHashedId, initializeBrand, liveStreamEventData, setSavedCustomizations]);

  const registrationCustomizations = useMemo<RegistrationCustomizationsWithGeneratedValues>(() => {
    if (isLoadingRegistrationCustomizations || isLoadingLiveStreamEventCustomizations) {
      return {} as RegistrationCustomizationsWithGeneratedValues;
    }

    return generateRegistrationThemeValues(registrationPageCustomizationsPreview);
  }, [
    isLoadingRegistrationCustomizations,
    isLoadingLiveStreamEventCustomizations,
    registrationPageCustomizationsPreview,
  ]);

  const fonts = useMemo(() => {
    const { titleFont, titleFontUrl, secondaryFont, secondaryFontUrl } = registrationCustomizations;
    const result: Font[] = [];
    if (titleFont) {
      result.push({ family: titleFont, url: titleFontUrl });
    }
    if (secondaryFont) {
      result.push({ family: secondaryFont, url: secondaryFontUrl });
    }
    return result;
  }, [registrationCustomizations]);

  const { isLoadingCustomFonts } = useInjectFonts({
    fonts,
    isWaitingForCustomizationsToLoad: isLoadingRegistrationCustomizations,
  });

  const cssVariablesOutput = useMemo(() => {
    if (isLoadingRegistrationCustomizations || isLoadingLiveStreamEventCustomizations) {
      return null;
    }

    return getThemeValuesAsCssVars(registrationCustomizations, 'event-registration');
  }, [
    isLoadingRegistrationCustomizations,
    isLoadingLiveStreamEventCustomizations,
    registrationCustomizations,
  ]);

  if (
    isLoadingRegistrationCustomizations ||
    isLoadingLiveStreamEventCustomizations ||
    isLoadingCustomFonts
  ) {
    return <LoadingSpinner />;
  }

  return (
    <RegistrationCustomizationsContext.Provider value={registrationCustomizations}>
      {isNotNil(cssVariablesOutput) ? <style type="text/css">{cssVariablesOutput}</style> : null}
      {children}
    </RegistrationCustomizationsContext.Provider>
  );
};

export const useRegistrationCustomizationsContext =
  (): RegistrationCustomizationsWithGeneratedValues =>
    useContext(RegistrationCustomizationsContext);
